import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Download, ArrowRight, Sparkles, FileText } from "lucide-react";
import api from "../lib/api";
import useSEO from "../hooks/useSEO";
import usePageContent from "../hooks/usePageContent";
import PageContent from "../components/PageContent";

export default function GuidePratique() {
  useSEO({
    title: "Guide pratique — Où aller, Qui faire",
    description: "Les guides pratiques DemkaBiz pour vous établir, entreprendre et vous orienter au Canada.",
  });
  const [guides, setGuides] = useState([]);
  const intro = usePageContent("guide.intro", "Une collection de guides pratiques pour vous accompagner dans vos démarches d'établissement, d'entrepreneuriat et de rencontre au Canada.");
  useEffect(() => {
    api.get("/guides").then((r) => setGuides(r.data)).catch(() => {});
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20" data-testid="guide-page">
      <p className="text-xs uppercase tracking-widest text-primary font-semibold mb-2">Médiathèque</p>
      <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-foreground tracking-tight leading-[1.05]">
        Guides pratiques <span className="text-primary">DemkaBiz</span>
      </h1>
      <p className="text-muted-foreground mt-4 max-w-2xl leading-relaxed" data-testid="guide-intro-text">
        <PageContent value={intro} />
      </p>

      {guides.length === 0 ? (
        <div className="mt-14 rounded-3xl border-2 border-dashed border-primary/20 p-10 text-center" data-testid="guides-empty">
          <FileText className="w-10 h-10 text-primary/60 mx-auto mb-3" />
          <p className="font-display font-semibold text-lg">Aucun guide disponible pour l'instant</p>
          <p className="text-sm text-muted-foreground mt-2">L'équipe DemkaBiz publie prochainement de nouveaux guides pratiques.</p>
        </div>
      ) : (
        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="guides-grid">
          {guides.map((g) => (
            <article key={g.id} className="card-surface flex flex-col overflow-hidden group" data-testid={`guide-card-${g.slug}`}>
              {g.cover_url && (
                <Link to={`/guide-pratique/${g.slug}`} className="block aspect-[3/4] bg-secondary overflow-hidden">
                  <img
                    src={g.cover_url}
                    alt={`Couverture — ${g.title}`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </Link>
              )}
              <div className="p-6 flex flex-col flex-1">
                {g.edition && (
                  <p className="text-xs uppercase tracking-widest text-accent font-semibold mb-2">{g.edition}</p>
                )}
                <h3 className="font-display font-semibold text-lg leading-snug">
                  <Link to={`/guide-pratique/${g.slug}`} className="hover:text-primary transition-colors">
                    {g.title}
                  </Link>
                </h3>
                {g.subtitle && <p className="text-sm text-muted-foreground mt-1">{g.subtitle}</p>}
                <p className="text-sm text-foreground/80 mt-3 leading-relaxed flex-1 line-clamp-4">{g.description}</p>
                <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-primary/5">
                  <Link
                    to={`/guide-pratique/${g.slug}`}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-primary text-white hover:bg-primary/90 text-sm font-medium transition"
                    data-testid={`guide-details-${g.slug}`}
                  >
                    En savoir plus <ArrowRight className="w-4 h-4" />
                  </Link>
                  {g.pdf_url && (
                    <a
                      href={g.pdf_url}
                      download
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-2 rounded-full bg-accent text-white hover:bg-accent/90 text-sm font-medium transition"
                      data-testid={`guide-download-${g.slug}`}
                    >
                      <Download className="w-4 h-4" /> Télécharger
                    </a>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <div className="mt-16 rounded-3xl bg-primary text-primary-foreground p-6 sm:p-10 grid md:grid-cols-[1fr_auto] gap-6 items-center">
        <div>
          <p className="text-xs uppercase tracking-widest text-accent font-semibold mb-2">Restez informé(e)</p>
          <h2 className="font-display font-bold text-2xl sm:text-3xl">Recevez nos nouveaux guides</h2>
          <p className="text-white/85 mt-2 text-sm">Créez votre compte pour être averti dès la parution de nos prochains guides pratiques.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/inscription" className="btn-orange" data-testid="guide-cta-register">
            Créer un compte <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/contact" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 hover:bg-white/20 border border-white/25 text-sm font-medium transition" data-testid="guide-cta-contact">
            Écrire à l'équipe
          </Link>
        </div>
      </div>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-surface p-5">
          <BookOpen className="w-6 h-6 text-primary mb-2" />
          <h3 className="font-display font-semibold text-base">Fiches pratiques</h3>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">Démarches administratives, santé, logement.</p>
        </div>
        <div className="card-surface p-5">
          <Download className="w-6 h-6 text-accent mb-2" />
          <h3 className="font-display font-semibold text-base">Téléchargement PDF</h3>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">Emportez le guide partout avec vous.</p>
        </div>
        <div className="card-surface p-5">
          <Sparkles className="w-6 h-6 text-primary mb-2" />
          <h3 className="font-display font-semibold text-base">Édition régulière</h3>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">Mise à jour saisonnière avec les nouveautés.</p>
        </div>
        <div className="card-surface p-5">
          <FileText className="w-6 h-6 text-accent mb-2" />
          <h3 className="font-display font-semibold text-base">100% francophone</h3>
          <p className="text-sm text-muted-foreground mt-1 leading-relaxed">Conçu pour la communauté francophone au Canada.</p>
        </div>
      </div>
    </div>
  );
}
